import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, User, CheckCircle2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';

/**
 * Public Beta signup — Phase 2 v5.14 P0.
 *
 * Posts to POST /api/v1/beta/signup which enforces:
 *   • Cloudflare Turnstile (siteverify on server)
 *   • Honeypot field (`website_url`) — CSS-hidden, real users never see it
 *   • Per-IP rate limit 3/min (slowapi)
 *   • Username uniqueness (case-insensitive, no enumeration)
 *   • Username 3–32 chars + alphanumeric + _ - .
 *   • Password ≥ 8 chars + bcrypt hash
 *
 * Brand voice: matches LoginPage (black background, cyan-400 accent,
 * "Actually not evil" tagline). Path: /signup
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

// Single shared loader so navigating between /login and /signup
// doesn't re-inject the script tag.
let _turnstileLoadPromise = null;
const ensureTurnstileLoaded = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.turnstile) return Promise.resolve();
  if (_turnstileLoadPromise) return _turnstileLoadPromise;

  _turnstileLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', resolve, { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }
    const s = document.createElement('script');
    s.src = TURNSTILE_SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return _turnstileLoadPromise;
};

export const SignupPage = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [honeypot, setHoneypot] = useState('');  // CSS-hidden; bot trap
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReady, setTurnstileReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const usernameRef = useRef(null);
  const widgetIdRef = useRef(null);
  const widgetContainerRef = useRef(null);

  useEffect(() => { usernameRef.current?.focus(); }, []);

  // Mount Turnstile widget once on first paint.
  useEffect(() => {
    let cancelled = false;
    ensureTurnstileLoaded()
      .then(() => {
        if (cancelled || !window.turnstile || !widgetContainerRef.current) return;
        if (widgetIdRef.current) return;  // already mounted
        widgetIdRef.current = window.turnstile.render(widgetContainerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: 'dark',
          callback: (tok) => { setTurnstileToken(tok); setTurnstileReady(true); },
          'error-callback': () => { setTurnstileToken(''); setTurnstileReady(false); },
          'expired-callback': () => { setTurnstileToken(''); setTurnstileReady(false); },
        });
      })
      .catch(() => { /* network blocked — submit will fail server-side, surfaced as a 400 */ });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile?.remove) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* noop */ }
      }
    };
  }, []);

  const resetTurnstile = () => {
    if (widgetIdRef.current && window.turnstile?.reset) {
      try { window.turnstile.reset(widgetIdRef.current); } catch { /* noop */ }
    }
    setTurnstileToken('');
    setTurnstileReady(false);
  };

  // ─── Light client-side validation (server is the authority) ─────────
  const usernamePattern = /^[A-Za-z0-9._-]{3,32}$/;
  const validate = () => {
    if (!usernamePattern.test(username.trim())) {
      return 'Username must be 3–32 characters: letters, numbers, "_", "-", ".".';
    }
    if (password.length < 8) {
      return 'Password must be at least 8 characters.';
    }
    if (password !== confirm) {
      return 'Passwords do not match.';
    }
    if (!turnstileToken) {
      return 'Please complete the verification challenge.';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setIsLoading(true);
    try {
      await axios.post(`${API}/v1/beta/signup`, {
        username: username.trim(),
        password,
        turnstile_token: turnstileToken,
        website_url: honeypot,  // ALWAYS empty for real users
      });
      setSuccess(true);
      toast.success('Account created. Welcome to the Beta.');
      // Brief celebration moment, then bounce to login.
      setTimeout(() => navigate('/login', { replace: true }), 1800);
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Could not create account. Please try again.');
      resetTurnstile();
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Success state ─────────────────────────────────────────────────
  if (success) {
    return (
      <div data-testid="signup-success" className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-full max-w-sm px-6 text-center">
          <CheckCircle2 className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-white mb-2">You're in.</h1>
          <p className="text-sm text-gray-400">Taking you to the sign-in page…</p>
        </div>
      </div>
    );
  }

  // ─── Default state ─────────────────────────────────────────────────
  return (
    <div data-testid="signup-page" className="min-h-screen bg-black flex items-center justify-center py-8">
      <div className="w-full max-w-sm px-6">
        <div className="text-center mb-8">
          <img
            src="/searchinc-logo.png"
            alt="Search.Inc — Actually not evil"
            style={{ height: 18 }}
            className="mx-auto"
            data-testid="signup-brand-logo"
          />
        </div>

        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-1 text-center">
          Create your Beta account
        </h2>
        <p className="text-[11px] text-gray-600 text-center mb-6">
          Username only — no email required. 3 searches + 1 demo to explore.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
          {/* Honeypot — bots auto-fill every input; real users never see this. */}
          <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
            <label>Website (do not fill)</label>
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              data-testid="signup-honeypot"
            />
          </div>

          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              ref={usernameRef}
              data-testid="signup-username"
              type="text"
              placeholder="Username (3–32 chars)"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              className="pl-10 bg-gray-950 border-gray-800 text-white placeholder:text-gray-600 focus:border-cyan-500 focus:ring-cyan-500/20"
            />
          </div>

          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              data-testid="signup-password"
              type="password"
              placeholder="Password (8+ chars)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              className="pl-10 bg-gray-950 border-gray-800 text-white placeholder:text-gray-600 focus:border-cyan-500 focus:ring-cyan-500/20"
            />
          </div>

          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              data-testid="signup-password-confirm"
              type="password"
              placeholder="Confirm password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              className="pl-10 bg-gray-950 border-gray-800 text-white placeholder:text-gray-600 focus:border-cyan-500 focus:ring-cyan-500/20"
            />
          </div>

          {/* Turnstile widget mount point */}
          <div className="flex justify-center pt-1" data-testid="signup-turnstile-container">
            <div ref={widgetContainerRef} />
          </div>

          <Button
            data-testid="signup-submit"
            type="submit"
            disabled={isLoading || !turnstileReady}
            className="w-full bg-cyan-600 hover:bg-cyan-500 text-white font-medium transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Creating account…' : (turnstileReady ? 'Create account' : 'Verifying you\'re human…')}
          </Button>

          <p className="text-[11px] text-gray-500 text-center pt-2">
            By creating an account you agree to our anonymous-by-default posture:
            no real name, no email, no third-party trackers.
          </p>
        </form>

        <div className="mt-6 pt-6 border-t border-gray-900 text-center">
          <p className="text-xs text-gray-500">
            Already have an account?{' '}
            <Link to="/login" className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2" data-testid="signup-back-to-login">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
